import {
    eachMonthOfInterval,
    endOfMonth,
    format,
    max,
    min,
    parseISO,
    startOfMonth,
    subDays,
} from "date-fns";
import { roundToCents } from "@common/helpers/monetary";
import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { RowSelectionState } from "@tanstack/react-table";
import {
    ScheduleApplicationParentRow,
    mapApplicationToRow,
} from "./scheduleApplicationRowHelpers";
import {
    getScheduleRowApplicationId,
    getUniqueScheduleApplicationIds,
} from "./scheduleApplicationRowId";

export interface ScheduleSelectionTotals {
    count: number;
    amount: number;
    remainingAmount: number;
}

export interface ScheduleSelectionMonth {
    monthKey: string;
    label: string;
    additions: number;
    recognized: number;
    remainingAmount: number;
}

const MAX_BREAKDOWN_MONTHS = 60;

export function getSelectedScheduleRows(options: {
    rows: ScheduleApplicationParentRow[];
    rowSelection: RowSelectionState;
}): ScheduleApplicationParentRow[] {
    return options.rows.filter((row) => options.rowSelection[row.id]);
}

export function summarizeSelectedSchedules(
    selectedRows: ScheduleApplicationParentRow[],
): ScheduleSelectionTotals {
    const scheduleCount = getUniqueScheduleApplicationIds(selectedRows).length;
    return {
        count: scheduleCount,
        amount: roundToCents(
            selectedRows.reduce((sum, row) => sum + row.amount, 0),
        ),
        remainingAmount: roundToCents(
            selectedRows.reduce((sum, row) => sum + row.remainingAmount, 0),
        ),
    };
}

function toDateOnly(date: Date): string {
    return format(date, "yyyy-MM-dd");
}

function resolveMonthRange(options: {
    selectedRows: ScheduleApplicationParentRow[];
    applicationsById: Map<string, ScheduleApplicationListItem>;
    periodStart: string | null;
    periodEnd: string | null;
}): { start: Date; end: Date } | null {
    const scheduleDates = options.selectedRows.flatMap((row) => {
        const application = options.applicationsById.get(
            getScheduleRowApplicationId(row),
        );
        return application
            ? [
                  application.termStart,
                  ...application.periods.map((period) => period.effectiveDate),
              ]
            : [];
    });
    if (scheduleDates.length === 0) {
        return null;
    }
    const parsedDates = scheduleDates.map((date) => parseISO(date));
    return {
        start: options.periodStart
            ? parseISO(options.periodStart)
            : min(parsedDates),
        end: options.periodEnd ? parseISO(options.periodEnd) : max(parsedDates),
    };
}

/**
 * A mini rollforward of the selection: what started, what was recognized and
 * what is still deferred at the end of each month. A display slice of a
 * multi-line schedule counts its share of the schedule.
 */
export function buildScheduleSelectionMonths(options: {
    selectedRows: ScheduleApplicationParentRow[];
    applications: ScheduleApplicationListItem[];
    periodStart: string | null;
    periodEnd: string | null;
}): ScheduleSelectionMonth[] {
    const applicationsById = new Map(
        options.applications.map((application) => [
            application.id,
            application,
        ]),
    );
    const range = resolveMonthRange({ ...options, applicationsById });
    if (!range || range.end < range.start) {
        return [];
    }
    const selections = options.selectedRows.flatMap((row) => {
        const application = applicationsById.get(
            getScheduleRowApplicationId(row),
        );
        if (!application || application.amount === 0) {
            return [];
        }
        return [{ row, application, share: row.amount / application.amount }];
    });
    const remainingAt = (date: string): number =>
        selections.reduce((sum, { application, share }) => {
            if (date < application.termStart) {
                return sum;
            }
            const asOf = mapApplicationToRow(application, { asOfDate: date });
            return sum + asOf.remainingAmount * share;
        }, 0);
    const additionsBetween = (from: string, to: string): number =>
        selections.reduce(
            (sum, { row, application }) =>
                application.termStart >= from && application.termStart <= to
                    ? sum + row.amount
                    : sum,
            0,
        );

    return eachMonthOfInterval(range)
        .slice(-MAX_BREAKDOWN_MONTHS)
        .map((month) => {
            const from = toDateOnly(max([startOfMonth(month), range.start]));
            const to = toDateOnly(min([endOfMonth(month), range.end]));
            const openingRemaining = remainingAt(
                toDateOnly(subDays(parseISO(from), 1)),
            );
            const additions = additionsBetween(from, to);
            const remainingAmount = remainingAt(to);
            return {
                monthKey: format(month, "yyyy-MM"),
                label: format(month, "MMM yyyy"),
                additions: roundToCents(additions),
                recognized: roundToCents(
                    openingRemaining + additions - remainingAmount,
                ),
                remainingAmount: roundToCents(remainingAmount),
            };
        });
}
